/**
 * Embedding a file, from the browser.
 *
 * The mirror of `courses.ts`, and the same order for the same reason: create,
 * upload, publish. An address that went live before the bytes behind it arrived
 * would be a broken link somebody may already have pasted into a chat.
 *
 * ── This is where the file leaves the vault ─────────────────────────────────
 *
 * `downloadStream` decrypts the node here, with the vault key this browser holds
 * and the server never does. What goes up in step 2 is the plaintext. That is
 * not a leak, it is the feature — an unfurler holds no key — but it is the one
 * moment worth naming in the code as well as in the dialog, because everything
 * else in this app is careful never to do it.
 */

import { api } from './api';
import { downloadStream, type DecryptedNode } from './nodes';

/** What the Worker will take. Bigger than any image and most short video, and
 *  small enough that an unfurler fetching it is not a bill. */
export const MAX_EMBED_BYTES = 100 * 1024 * 1024;

export interface EmbedResult {
  id: string;
  slug: string;
  /** The address to paste. */
  pageUrl: string;
  /** The bytes themselves, for an `<img src>`. */
  fileUrl: string;
}

/**
 * The public origin, derived from this one.
 *
 * `cloud.` → `embed.`, exactly as `courseUrl` swaps in `courses.`, so a staging
 * deployment produces a staging address rather than inventing a production one.
 */
export function embedOrigin(): string {
  const { protocol, hostname, port } = window.location;
  const host = hostname.startsWith('cloud.')
    ? `embed.${hostname.slice('cloud.'.length)}`
    : hostname === 'localhost' || hostname === '127.0.0.1'
      ? hostname
      : 'embed.mauvely.com';
  // A local stack has one Worker on one port answering for every hostname, so
  // the port has to survive; production never has one.
  const suffix = port && (hostname === 'localhost' || hostname === '127.0.0.1') ? `:${port}` : '';
  return `${protocol}//${host}${suffix}`;
}

/**
 * Take a copy of `node` out of the vault and give it a public address.
 *
 * `title` is what the unfurl shows and what the address is built from. The
 * dialog passes the file's own name, which means embedding
 * `invoice-final-FINAL-2.pdf` publishes that name — so the dialog says so
 * before the button is pressed rather than after. A separate title is the
 * obvious improvement and is not worth a second field until somebody asks.
 */
export async function embedFile(
  node: DecryptedNode,
  nodeKey: CryptoKey,
  title: string,
  mime: string,
  onProgress?: (done: number, total: number) => void
): Promise<EmbedResult> {
  if (node.plainSizeBytes > MAX_EMBED_BYTES) {
    throw new Error('That file is too large to embed.');
  }

  // The plan is checked here, before anything is decrypted: somebody who cannot
  // do this should be told before they spend a minute on it.
  const { id, slug } = await api.embeds.create({ nodeId: node.id, title, mime });

  // **The moment it leaves the vault.** Decrypted in this tab, with a key the
  // server has never held, and sent up as plaintext.
  const stream = await downloadStream(node.id, nodeKey, onProgress);
  const bytes = new Uint8Array(await new Response(stream).arrayBuffer());

  try {
    await api.embeds.putContent(id, bytes);
    await api.embeds.publish(id);
  } catch (err) {
    // A half-made embed is a row in `preparing` that serves nothing. Revoking
    // it leaves the slug spent, which is correct — it was handed out to
    // whatever the failure was — and leaves nothing that could go live later.
    await api.embeds.remove(id).catch(() => {});
    throw err;
  }

  const origin = embedOrigin();
  return { id, slug, pageUrl: `${origin}/e/${slug}`, fileUrl: `${origin}/f/${slug}` };
}
